import { Suspense, lazy } from "react";
import Seo from "@/components/Seo";
import { SITE_URL } from "@/lib/seo";
import { useLanguage } from "@/contexts/LanguageContext";
import type { Locale } from "@/content/types";
import { clinicNode } from "@/lib/schema";
import HeroSection from "@/components/HeroSection";
import ServicesSection from "@/components/ServicesSection";
import TechnologySection from "@/components/TechnologySection";
import WhyChooseUs from "@/components/WhyChooseUs";
import QuickActionBar from "@/components/QuickActionBar";
import { galleryItems, videoGalleryItems } from "@/lib/galleryData";

// Below-the-fold sections are code-split so their weight (carousels, dialogs,
// social embeds) doesn't load until the browser is done with the initial paint.
const Gallery = lazy(() => import("@/components/Gallery"));
const VideoGallery = lazy(() => import("@/components/VideoGallery"));
const ReviewsSection = lazy(() => import("@/components/ReviewsSection"));
const SocialFeeds = lazy(() => import("@/components/SocialFeeds"));
const ContactSection = lazy(() => import("@/components/ContactSection"));

const METADATA: Record<Locale, { title: string; description: string; twitterTitle: string }> = {
  en: {
    title: "Best Dental Clinic in Nasr City Cairo | Primero Dental Clinic",
    description:
      "Primero Dental Clinic in Nasr City, Cairo — digital dentistry, dental implants, orthodontics and cosmetic treatments. Book a consultation with our team.",
    twitterTitle: "Primero Dental Clinic | Dentist in Nasr City, Cairo",
  },
  ar: {
    title: "أفضل عيادة أسنان في مدينة نصر | بريميرو لطب الأسنان",
    description:
      "عيادة بريميرو لطب الأسنان في مدينة نصر، القاهرة — زراعة الأسنان، تقويم الأسنان، وطب الأسنان التجميلي. احجز استشارتك مع فريقنا.",
    twitterTitle: "بريميرو لطب الأسنان | طب أسنان في مدينة نصر",
  },
};

const Index = ({ locale }: { locale: Locale }) => {
  const { t } = useLanguage();
  const meta = METADATA[locale];
  return (
    <>
      {/*
        Routed through the shared <Seo> component rather than a hand-rolled
        <Head>. The bespoke block this replaces was missing several tags the
        rest of the site emits — no <html dir>, no og:image:alt/width/height,
        no twitter:site, no rel=alternate — because it had drifted from
        Seo.tsx. The separate googlebot/bingbot "index, follow" tags are also
        gone: they only restated the generic robots directive, which every
        crawler including Googlebot and Bingbot already honours.
      */}
      <Seo
        title={meta.title}
        description={meta.description}
        path={`/${locale}`}
        locale={locale}
        keywords="dental clinic nasr city, best dental clinic cairo, dentist nasr city, cosmetic dentistry egypt, dental implants cairo, hollywood smile cairo, orthodontics nasr city, laser dentistry egypt, dental clinic near me cairo"
        twitterTitle={meta.twitterTitle}
        imageAlt="The treatment area at Primero Dental Clinic in Nasr City, Cairo"
        geo
        schema={[
          clinicNode(locale),
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "@id": `${SITE_URL}/#website`,
            url: SITE_URL,
            name: meta.title,
            description: meta.description,
            publisher: { "@id": `${SITE_URL}/#clinic` },
            inLanguage: ["en-US", "ar-EG"],
          },
        ]}
      />

      <main className="min-h-screen bg-background">
      {/* Navbar and Footer are mounted once in src/components/Layout.tsx. */}
        <HeroSection />
        <div id="services">
          <ServicesSection />
        </div>
        <TechnologySection />
        <div id="why-choose">
          <WhyChooseUs />
        </div>
        <Suspense fallback={null}>
          <section id="gallery" className="py-24 bg-background px-4">
            <div className="container mx-auto">
              <div className="max-w-3xl mb-16 px-4 md:px-0">
                <span className="text-primary text-sm font-semibold tracking-widest uppercase block mb-3">{t('lifeAtPrimero')}</span>
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">{t('whereSmilesBegin')}</h2>
                <p className="text-slate-400 text-lg">{t('warmAtmosphereDesc')}</p>
              </div>
              <Gallery
                items={galleryItems}
                showFilters={true}
              />
            </div>
          </section>

          <div id="video-review">
            <VideoGallery
              videos={videoGalleryItems}
            />
          </div>

          <div id="reviews">
            <ReviewsSection />
          </div>
          <SocialFeeds />
          <div id="location">
            <ContactSection />
          </div>
        </Suspense>
        <QuickActionBar />
      </main>
    </>
  );
};

export default Index;
